import { ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import Stepper from "./Stepper";
import { useSelectEntry } from "../hooks/useSelectEntry";
import { canNavigateToStep, isStepAccessible, isStepCompleted } from "../stores/formCompletionStore";
import FormLoading from "./FormLoading";
import { useCopy } from "../i18n/locale";

const FORM_STEP_ROUTES = [
    { key: "at_sea", route: "/form/at-sea" },
    { key: "at_sea_all_day", route: "/form/at-sea-all-day" },
    { key: "at_sea_intervals", route: "/form/at-sea-intervals" },
    { key: "on_duty_intervals", route: "/form/on-duty-intervals" },
    { key: "glucose_issue", route: "/form/glucose-issue" },
    { key: "notes", route: "/form/notes" },
];

interface FormStepWrapperProps {
    children: ReactNode;
    currentStep: number;
}

export default function FormStepWrapper({ children, currentStep }: FormStepWrapperProps) {
    const { entry_id, profile_id } = useSearch({ from: "/form" });
    const { data: entryData, isLoading, error } = useSelectEntry(entry_id);
    const navigate = useNavigate();
    const copy = useCopy().steps;
    const formSteps = [
        { ...FORM_STEP_ROUTES[0], words: copy.atSea },
        { ...FORM_STEP_ROUTES[1], words: copy.allDay },
        { ...FORM_STEP_ROUTES[2], words: copy.timeAtSea },
        { ...FORM_STEP_ROUTES[3], words: copy.onDuty },
        { ...FORM_STEP_ROUTES[4], words: copy.glucose },
        { ...FORM_STEP_ROUTES[5], words: copy.notes },
    ];

    const getStepsWithState = () => {
        return formSteps.map((step, index) => {
            let stage: "past" | "current" | "future" | "skipped";
            let accessible = true;
            let status = "";
            let canNavigate = false;

            if (entryData) {
                const completed = isStepCompleted(entryData, index);
                const stepAccessible = isStepAccessible(entryData, index);
                const isSubmitted = entryData.submitted_at;

                // Special logic for steps 1 and 2 (all day and time at sea/harbour)
                const shouldSkipSeaSteps = (index === 1 || index === 2) && entryData.at_sea === false;
                const shouldSkipTimeRange = index === 2 && entryData.at_sea_all_day === true;
                const shouldSkipWorkSchedule = index === 3 && entryData.at_sea === false;
                const shouldBeSkipped = shouldSkipSeaSteps || shouldSkipTimeRange || shouldSkipWorkSchedule;

                // Calculate stage and status
                if (index === currentStep) {
                    stage = "current";
                    status = copy.inProgress;
                } else if (isSubmitted && index === 4) {
                    stage = "past";
                    status = copy.completed;
                } else if (completed) {
                    stage = "past";
                    status = copy.completed;
                } else if (shouldBeSkipped) {
                    stage = "skipped";
                    status = copy.skipped;
                } else if (!stepAccessible && !shouldBeSkipped) {
                    // For steps 1 and 2, show as pending when at_sea is not yet set
                    stage = "future";
                    status = copy.pending;
                } else {
                    stage = "future";
                    status = copy.pending;
                }

                // Calculate navigation permissions
                accessible = stepAccessible;
                canNavigate = canNavigateToStep(currentStep, index, entryData);
            } else {
                // Default states when no entry data
                if (index === currentStep) {
                    stage = "current";
                    status = copy.inProgress;
                    canNavigate = true;
                } else if (index < currentStep) {
                    stage = "past";
                    status = copy.completed;
                    canNavigate = true; // Can always go back
                } else {
                    // Show steps 1 and 2 as pending initially
                    stage = "future";
                    status = copy.pending;
                    canNavigate = false;
                }
            }

            return { ...step, stage, accessible, status, canNavigate };
        });
    };

    const steps = getStepsWithState();

    const handleStepChange = (stepIndex: number) => {
        const step = steps[stepIndex];
        if (step.route && (step.canNavigate || step.stage === "past" || step.stage === "current")) {
            navigate({
                to: step.route,
                search: { entry_id, profile_id },
            });
        }
    };

    return (
        <section className="space-y-8">
            <Stepper steps={steps} onStepChange={handleStepChange} />
            {isLoading && <FormLoading />}
            {error && (
                <div className="flex justify-center items-center min-h-[300px]">
                    <div className="text-red-500">{copy.loadError}</div>
                </div>
            )}
            {!isLoading && !error && (
                <div className="space-y-8">
                    <div className="flex justify-center items-center min-h-[300px]">{children}</div>

                    <div className="flex justify-center items-center pt-6">
                        <div className="text-gray-500 text-sm">{copy.progress(currentStep + 1, formSteps.length)}</div>
                    </div>
                </div>
            )}
        </section>
    );
}
